import type { Container } from '@playcanvas/pcui';
import { Entity, Mat4, Quat, TranslateGizmo, Vec3 } from 'playcanvas';

import { createDistanceScaleOp } from './distance-scale';
import { Events } from '../events';
import { Scene } from '../scene';
import type { Point3 } from '../spatial-guides-data';
import { Splat } from '../splat';
import { ToolOverlay, OverlayWriter } from '../tool-overlay';
import { Transform } from '../transform';
import { DimensionLabels } from '../ui/dimension-labels';

// pointer movement below this many pixels still counts as a click
const CLICK_TOLERANCE = 4;
const finite = (point: Vec3) => Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z);

const mat = new Mat4();
const p = new Vec3();
const p0 = new Vec3();
const p1 = new Vec3();
const bmin = new Vec3();
const bmax = new Vec3();

const t = new Transform();

class MeasureTransformHandler {
    activate() {}
    deactivate() {}
}

class MeasureTool {
    activate: () => void;
    deactivate: () => void;
    getFocus: () => { position: Vec3, radius: number } | null;

    constructor(events: Events, scene: Scene, canvasContainer: Container, annotationParent: HTMLElement, calibrate = false) {
        // the length label along the measured line (shown when 'show
        // dimensions' is enabled); the points and line render in the scene
        // via the shared tool overlay
        const dimLabels = new DimensionLabels(scene, canvasContainer.dom, annotationParent, calibrate ? 'distance-scale-svg' : 'measure-tool-svg', 1);

        const mode = calibrate ? 'distanceScale' : 'measure';
        let knownLength = 1;
        const pendingReframes = new WeakSet<object>();
        let state = { count: 0, length: 0, hasSelection: false, knownLength };
        events.function(`${mode}.state`, () => state);

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        const entity = new Entity('measureGizmoPivot');
        scene.app.root.addChild(entity);
        const transformHandler = new MeasureTransformHandler();

        let active = false;
        let revision = 0;
        let splat: Splat;
        let pointState: { measurePoints: Vec3[], measureSelection: number };

        // get world space point
        const getPoint = (index: number, result: Vec3) => {
            splat.worldTransform.transformPoint(pointState.measurePoints[index], result);
        };

        const getPoint2d = (index: number, result: Vec3) => {
            getPoint(index, result);
            scene.camera.worldToScreen(result, result);
            result.x *= canvasContainer.dom.clientWidth;
            result.y *= canvasContainer.dom.clientHeight;
        };

        // the measurement points and line render in the scene via the shared
        // tool overlay (occluded by gaussians, with a faint ghost showing through)
        const overlay = new ToolOverlay();
        const visible = () => {
            const guides = events.invokeOptional('spatialGuides');
            return calibrate ? guides?.referenceVisible !== false : guides?.measurementsVisible !== false && guides?.distanceVisible !== false;
        };
        overlay.provider = (writer: OverlayWriter) => {
            if (!splat || !visible()) {
                return;
            }
            const count = pointState.measurePoints.length;
            const pts = [p0, p1];
            for (let i = 0; i < count; i++) {
                getPoint(i, pts[i]);
                writer.dot(pts[i]);
            }
            if (count === 2) {
                writer.segment(p0, p1);
            }
        };
        scene.add(overlay);

        const updateVisuals = () => {
            gizmo.detach();

            if (splat && active && visible() && pointState.measureSelection >= 0 && pointState.measureSelection < pointState.measurePoints.length) {
                getPoint(pointState.measureSelection, p);
                t.set(p, Quat.IDENTITY, Vec3.ONE);
                events.invoke('pivot').place(t);
                entity.setLocalPosition(p);
                gizmo.attach([entity]);
            }

            let length = 0;
            if (splat && pointState.measurePoints.length === 2) {
                getPoint(0, p0);
                getPoint(1, p1);
                length = p0.distance(p1);
            }
            state = { count: splat ? pointState.measurePoints.length : 0, length, hasSelection: !!splat, knownLength };
            events.fire(`${mode}.state`, state);
        };

        events.on(`${mode}.clear`, () => {
            revision++;
            if (!splat) return;
            pointState.measurePoints.length = 0;
            pointState.measureSelection = -1;
            updateVisuals();
            scene.forceRender = true;
        });
        if (calibrate) {
            events.on('distanceScale.length', (value: number) => {
                knownLength = value;
                updateVisuals();
            });
            events.on('distanceScale.apply', () => {
                if (!active || !splat || pointState.measurePoints.length !== 2) return;
                getPoint(0, p0);
                getPoint(1, p1);
                const op = createDistanceScaleOp(scene, p0, p1, knownLength);
                if (op) {
                    pendingReframes.add(op);
                    events.fire('edit.add', op);
                }
            });
        } else {
            events.on('measure.save', (name: string) => {
                if (!active || !splat || pointState.measurePoints.length !== 2) return;
                getPoint(0, p0);
                getPoint(1, p1);
                events.fire('measurement.save', p0.clone(), p1.clone(), name);
            });
        }

        events.on('edit.apply', (op: object) => {
            if (active) updateVisuals();
            if (pendingReframes.delete(op)) {
                // History applies edits asynchronously. Frame the updated scene,
                // rather than camera.focus's active-tool reference-point target.
                events.fire('camera.setControlMode', 'orbit');
                const bound = scene.bound;
                scene.camera.focus({ focalPoint: bound.center, radius: bound.halfExtents.length(), speed: 1 });
            }
        });
        events.function(`${mode}.load`, (coordinates: Point3[]) => {
            if (!splat) return false;
            revision++;
            mat.invert(splat.worldTransform);
            pointState.measurePoints = coordinates.map(point => mat.transformPoint(new Vec3(point)));
            pointState.measureSelection = 0;
            updateVisuals();
            return true;
        });
        events.on('measurement.deselect', () => {
            if (pointState) pointState.measureSelection = -1;
            revision++;
            updateVisuals();
        });
        events.on('spatialGuides.changed', () => {
            if (active) updateVisuals(); scene.forceRender = true;
        });

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        let transforming = false;
        let clicked = false;
        gizmo.on('transform:start', () => {
            transforming = true;
            clicked = false;
            events.invoke('pivot').start();
        });

        gizmo.on('transform:move', () => {
            events.invoke('pivot').moveTRS(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
        });

        gizmo.on('transform:end', () => {
            transforming = false;
            events.invoke('pivot').end();
        });

        events.on('selection.changed', (selection: Splat) => {
            // End editing against the old layer before replacing its draft state.
            if (active) events.fire('tool.deactivate');
            splat = selection;
            pointState = selection && (calibrate ? selection.scaleReferencePoints : selection);
            if (pointState) {
                pointState.measurePoints = pointState.measurePoints.filter(finite);
                pointState.measureSelection = Math.min(pointState.measureSelection, pointState.measurePoints.length - 1);
            }
            updateVisuals();
        });

        events.on('pivot.moved', () => {
            if (active && splat && pointState.measureSelection >= 0 && pointState.measureSelection < pointState.measurePoints.length) {
                const p = events.invoke('pivot').transform.position;
                if (!finite(p)) return;
                mat.invert(splat.worldTransform);
                const local = mat.transformPoint(p, new Vec3());
                if (finite(local)) pointState.measurePoints[pointState.measureSelection].copy(local);
            }
            scene.forceRender = true;
        });

        events.on('pivot.ended', () => {
            if (!transforming && active && splat && pointState.measureSelection >= 0 && pointState.measureSelection < pointState.measurePoints.length) {
                updateVisuals();
            }
        });

        events.on('select.delete', () => {
            if (!transforming && active && splat && pointState.measureSelection >= 0 && pointState.measureSelection < pointState.measurePoints.length) {
                pointState.measurePoints.splice(pointState.measureSelection, 1);
                pointState.measureSelection--;
                updateVisuals();
            }
        });

        const isPrimary = (e: PointerEvent) => {
            return e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
        };

        let clickX = 0;
        let clickY = 0;

        const pointerdown = (e: PointerEvent) => {
            if (!transforming && !clicked && isPrimary(e)) {
                clicked = true;
                clickX = e.offsetX;
                clickY = e.offsetY;
            }
        };

        const pointermove = (e: PointerEvent) => {
            // forgive small jitter between down and up; only a real drag cancels the click
            if (clicked && Math.hypot(e.offsetX - clickX, e.offsetY - clickY) > CLICK_TOLERANCE) {
                clicked = false;
            }
        };

        const pointerup = async (e: PointerEvent) => {
            if (splat && visible() && clicked && isPrimary(e)) {
                clicked = false;

                let closestIdx = -1;

                // check for intersection with existing point
                const cameraPos = scene.camera.mainCamera.getPosition();
                const cameraFwd = scene.camera.mainCamera.forward;
                for (let i = 0; i < pointState.measurePoints.length; i++) {
                    // ignore points behind the camera (their projection is mirrored)
                    getPoint(i, p);
                    if (p.sub(cameraPos).dot(cameraFwd) <= 0) {
                        continue;
                    }

                    getPoint2d(i, p);

                    if (Math.abs(p.x - clickX) < 8 && Math.abs(p.y - clickY) < 8) {
                        closestIdx = i;
                        break;
                    }
                }

                if (closestIdx >= 0) {
                    pointState.measureSelection = closestIdx;
                    updateVisuals();
                    return;
                }

                // place at the pointer-down position: that is where the user aimed
                if (pointState.measurePoints.length < 2) {
                    const target = splat;
                    const pickRevision = revision;
                    const result = await scene.camera.intersect(clickX / canvasContainer.dom.clientWidth, clickY / canvasContainer.dom.clientHeight);
                    // another click may have landed a point while the pick was in flight
                    if (result && finite(result.position) && active && pickRevision === revision && splat === target && pointState.measurePoints.length < 2) {
                        mat.invert(splat.worldTransform);
                        mat.transformPoint(result.position, p);
                        if (!finite(p)) return;
                        pointState.measureSelection = pointState.measurePoints.length;
                        pointState.measurePoints.push(p.clone());
                        updateVisuals();
                    }
                }

                e.preventDefault();
                e.stopPropagation();
            }
        };

        // length label along the measured line
        events.on('postrender', () => {
            // the svg is hidden while the tool is inactive, so skip the work
            if (!splat) {
                return;
            }

            if (!visible() || pointState.measurePoints.length !== 2 || (!calibrate && !events.invoke('camera.boundDimensions'))) {
                dimLabels.hideLabel(0);
                return;
            }

            getPoint(0, p0);
            getPoint(1, p1);
            dimLabels.setLabel(0, p0, p1);
        });

        // re-render so the label reacts to the setting while the tool is active
        events.on('camera.boundDimensions', () => {
            if (active) {
                scene.forceRender = true;
            }
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        // frame the placed points instead of the selection ('f' shortcut)
        this.getFocus = () => {
            const count = splat ? pointState.measurePoints.length : 0;
            if (count === 0) {
                return null;
            }

            // the points' world aabb, framed like the selection bound: its
            // center at half its diagonal
            getPoint(0, bmin);
            bmax.copy(bmin);
            for (let i = 1; i < count; i++) {
                getPoint(i, p);
                bmin.min(p);
                bmax.max(p);
            }
            const position = new Vec3().add2(bmin, bmax).mulScalar(0.5);
            let radius = bmax.sub(bmin).length() * 0.5;

            // a lone point has no extent: center it and keep the current zoom
            if (radius === 0) {
                radius = scene.camera.distance * scene.camera.sceneRadius;
            }

            return { position, radius };
        };

        this.activate = () => {
            active = true;
            if (calibrate && splat && pointState.measurePoints.length === 0) {
                const reference = events.invoke('spatialGuides').measurements.find((measurement: { id: string }) => measurement.id === 'scale-reference');
                if (reference) {
                    mat.invert(splat.worldTransform);
                    pointState.measurePoints.push(mat.transformPoint(new Vec3(reference.start)), mat.transformPoint(new Vec3(reference.end)));
                    pointState.measureSelection = 0;
                    knownLength = new Vec3(reference.start).distance(new Vec3(reference.end));
                }
            }
            updateVisuals();
            events.fire('measurement.ensureVisible', calibrate ? 'reference' : 'distance');
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);

            dimLabels.show();

            events.fire('transformHandler.push', transformHandler);


            scene.forceRender = true;
        };

        this.deactivate = () => {
            active = false;
            clicked = false;
            revision++;
            if (pointState) pointState.measureSelection = -1;

            updateVisuals();
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);

            dimLabels.show();

            events.fire('transformHandler.pop');

            scene.forceRender = true;
        };
    }
}

export { MeasureTool };
